// src/modules/app-views/goodsReceipt/pages/Init/modals/NfeImportModal/Step1.jsx
// *******************************************************************************
// NFE IMPORT MODAL - STEP 1: FILE UPLOAD
// *******************************************************************************
// Drag & drop area for selecting XML, PDF, or image files.
// Captures file in Form.Item so Step 2 can read it via form.getFieldValue.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback } from 'react';
import { Upload, Typography, Form, Flex } from 'antd';
import {
  FileTextOutlined,
  FilePdfOutlined,
  CameraOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import translateX from 'utils/translateX';

const { Dragger } = Upload;
const { Text } = Typography;

// *******************************************************************************
// STEP FUNCTION
// *******************************************************************************
const step1Upload = () => ({
  stepTitle: translateX('select_fiscal_document'),
  stepTooltip: translateX('select_fiscal_document'),
  validationFields: ['file'],
  content: <Step1Content />,
});

// *******************************************************************************
// STEP CONTENT COMPONENT
// *******************************************************************************
const Step1Content = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const beforeUpload = useCallback(() => {
    // Prevent automatic upload — we handle the file manually
    return false;
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '0 8px' }}>
      <Form.Item
        name="file"
        valuePropName="fileList"
        getValueFromEvent={normFile}
        rules={[{ required: true, message: translateX('required') }]}
        style={{ marginBottom: 12 }}
      >
        <Dragger
          accept=".xml,.pdf,.jpg,.jpeg,.png"
          maxCount={1}
          beforeUpload={beforeUpload}
          style={{ padding: '20px 0' }}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            {translateX('drag_or_click_to_upload')}
          </p>
          <p className="ant-upload-hint">
            {translateX('supported_formats')}: XML, PDF, JPG, PNG
          </p>
        </Dragger>
      </Form.Item>

      <Flex vertical gap={4}>
        <Text type="secondary">
          <FileTextOutlined style={{ marginRight: 4 }} />
          <strong>XML</strong> — {translateX('xml_import_description')}
        </Text>
        <Text type="secondary">
          <FilePdfOutlined style={{ marginRight: 4 }} />
          <strong>PDF</strong> — {translateX('pdf_import_description')}
        </Text>
        <Text type="secondary">
          <CameraOutlined style={{ marginRight: 4 }} />
          <strong>{translateX('image')}</strong> — {translateX('image_import_description')}
        </Text>
      </Flex>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default step1Upload;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Normalize Upload event to fileList for Form.Item
 */
function normFile(e) {
  if (Array.isArray(e)) return e;
  return e?.fileList;
}
